import { Calendar } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '../../lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/Popover';
import { CalendarMonth } from './CalendarMonth';
import { addMonths, formatDateLabel, monthBounds, todayISO, viewOf } from './helpers';
import type { DayMark, DayState, ISODate } from './types';

export type DatePickerProps = {
  /** Selected date as 'yyyy-mm-dd', or '' when none. Controlled. */
  value: ISODate | '';
  onChange: (value: ISODate) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  /** Override "today" for deterministic rendering/tests. */
  today?: ISODate;
  /** Latest selectable day; later days render disabled. Use for dates that
   *  describe something that already happened — a day cannot be reported on
   *  before it has been lived. */
  maxDate?: ISODate;
  align?: 'start' | 'center' | 'end';
  className?: string;
  /** Days to dot — e.g. the ones already analysed. */
  markedDates?: readonly ISODate[];
  /** What the dots mean. Defaults to 'success'. */
  markVariant?: DayMark;
  /** Fires with the visible month's bounds so a caller can mark it. */
  onViewChange?: (from: ISODate, to: ISODate) => void;
};

export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  disabled,
  id,
  ariaLabel,
  today,
  maxDate,
  align = 'start',
  className,
  markedDates,
  markVariant = 'success',
  onViewChange,
}: DatePickerProps) {
  const todayVal = today ?? todayISO();
  const marked = new Set(markedDates ?? []);

  // Held in a ref so an inline callback cannot re-fire the effect every render.
  const notifyView = useRef(onViewChange);
  notifyView.current = onViewChange;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => viewOf(value || todayVal));

  // ISO dates sort lexicographically, so a string compare is the whole bound.
  const dayProps = (iso: ISODate): DayState => ({
    fill: iso === value ? 'edge' : null,
    ring: false,
    today: iso === todayVal,
    marked: marked.has(iso) ? markVariant : null,
    disabled: maxDate !== undefined && iso > maxDate,
  });

  useEffect(() => {
    if (!open) return;
    const [from, to] = monthBounds(view.year, view.monthIdx);
    notifyView.current?.(from, to);
  }, [open, view.year, view.monthIdx]);

  const select = (iso: ISODate) => {
    onChange(iso);
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        // Re-anchor on open so the calendar always lands on the selected month
        // rather than wherever the user last browsed to.
        if (next) setView(viewOf(value || todayVal));
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            'flex h-8 w-full items-center gap-2 rounded-8 border border-control-border bg-control px-3 text-sm transition-colors',
            'focus-visible:border-focus focus-visible:outline-none',
            disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:bg-control-hover',
            value ? 'text-control-foreground' : 'text-control-placeholder',
            className,
          )}
        >
          <Calendar className="size-4 shrink-0 text-control-placeholder" />
          <span className="truncate">{value ? formatDateLabel(value) : placeholder}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-auto rounded-lg border-date-border-faint bg-date-popover p-3"
      >
        <CalendarMonth
          year={view.year}
          monthIdx={view.monthIdx}
          onPrev={() => setView(addMonths(view.year, view.monthIdx, -1))}
          onNext={() => setView(addMonths(view.year, view.monthIdx, 1))}
          onSelect={select}
          dayProps={dayProps}
        />
      </PopoverContent>
    </Popover>
  );
}
